import { Component, OnInit } from '@angular/core';
import { Entry } from 'contentful';
import { entraAuthService } from 'src/app/service/auth.service';
import { ContentfulService } from 'src/app/service/contentful.service';
import { PageContentSearchService } from 'src/app/service/page-content-search.service';
import { GroupValidator } from 'src/app/validator/group-validator';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';
import { HomePage } from '../../../model/page/home-page';

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
})
export class HomePageComponent implements OnInit {
  homePage: HomePage = {} as HomePage;
  NewsCardTitle: string = '';
  NewsCardImage: string = '';
  SeeAllNews: string = '';
  NewsUrl: string = '';
  UsefulLinks: string = '';
  LatestAPIProductUpdates: string = '';
  NDCCreditVouchers: string = '';
  RevisionNoticeButton: string = '';
  RevisionNoticeUrl: string = '';
  revisionNoticeTitle: string = '';
  readonly MAX_ENTRIES_TABLE: number = 5;
  constructor(private contentfulService: ContentfulService, public authService: entraAuthService, 
    private pageContentSearchService: PageContentSearchService) {}

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'home') {
        this.homePage.htmlTitle = title.htmlTitle;
      }
    });

    this.pageContentSearchService.fetchSearchResultsFromContentful();
    this.homePage.sections = [];
    this.homePage.noteSectionsForAnnouncements = [];
    this.homePage.sectionsJustBelowBanner = [];
    this.homePage.sectionsJustBelowTable = [];
    this.homePage.tableSections = [];
    this.homePage.tableSectionsJustBelowVoucher = [];
    this.homePage.userCards = [];
    this.homePage.creditVoucherCards = [];
    this.homePage.usefulLinkCards = [];
    this.homePage.newsSectionCards = [];
    this.homePage.revisionNotices = [];

    const query = { 'fields.title': 'HOME', include: 2 };
    this.contentfulService.getPageContentResponse(query).then((homePageEntries) => {
      this.mapHtmlTitle(homePageEntries.items);
      this.mapBanner(homePageEntries.includes.Entry);
      this.mapNewsCardLabels();
      this.noteSections(homePageEntries.includes.Entry);
      this.mapSections(homePageEntries.includes.Entry);
      this.mapCards(homePageEntries.includes.Entry);
    });
    const latestAPIProductUpdates = { 'fields.title': 'NDC Product Update Notifications' };
    this.contentfulService.getPageContentWithEntries(latestAPIProductUpdates).then((homePageEntries) => {
      this.mapPages(homePageEntries);
    });
    this.contentfulService.getRevisionNoticePageResponse().then((homePageEntries) => {
      this.mapPages(homePageEntries);
    });
  }

  private mapHtmlTitle(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      this.homePage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  private mapBanner(homePageEntries: Entry<any>[]) {
    homePageEntries
      .filter((entry) => ModelValidator.isBanner(entry))
      .forEach((entry) => {
        this.mapEntryToBanner(entry);
      });
  }

  private mapEntryToBanner(entry: Entry<any>) {
    let imageDetails: any = entry.fields['image'];
    let additionalImage1Details: any = entry.fields['additionalImage1'];
    let additionalImage2Details: any = entry.fields['additionalImage2'];
    let videoDetails: any = entry.fields['video'];
    let patternDetails: any = entry.fields['pattern'];
    this.homePage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: imageDetails.fields.file.url,
      additionalImage1: additionalImage1Details.fields.file.url,
      additionalImage2: additionalImage2Details.fields.file.url,
      video: videoDetails?.fields?.file?.url,
      pattern: patternDetails.fields.file.url
    };
  }

  private noteSections(homePageEntries: Entry<any>[]) {
    homePageEntries
      .filter((entry) => ModelValidator.isNote(entry))
      .forEach((entry) => {
        if (GroupValidator.isAnnouncemnet(entry.fields['group'] as string)) {
          this.mapEntryToNoteSection(entry);
        }
      });
  }

  private mapEntryToNoteSection(entry: Entry<any>) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();
    this.homePage.noteSectionsForAnnouncements.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      category: entry.fields['category'] as string,
      order: entry.fields['order'] as number,
      fullTitle: entry.fields['fullTitle'] as string,
      iconClass: entry.fields['iconClass'] as string
    });
    this.homePage.noteSectionsForAnnouncements.sort((a, b) => a.order - b.order);
  }

  private mapSections(homePageEntries: Entry<any>[]) {
    homePageEntries
      .filter((entry) => ModelValidator.isSection(entry))
      .forEach((entry) => {
        this.mapEntryToSection(entry);
      });

    this.homePage.sectionsJustBelowBanner = this.homePage.sections.filter((section) => GroupValidator.isJustBelowBanner(section.group));
    this.homePage.sectionsJustBelowTable = this.homePage.sections.filter((section) => GroupValidator.isJustBelowTable(section.group));

    this.homePage.sectionsJustBelowBanner.sort((a, b) => a.order - b.order);
    this.homePage.sectionsJustBelowTable.sort((a, b) => a.order - b.order);
  }

  private mapEntryToSection(entry: Entry<any>) {
    let givenTitle: any = entry.fields['title'] as string;
    let pageTitle = givenTitle.split('#').pop();
    this.homePage.sections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number
    });
  }

  private mapPages(homePageEntries: Entry<any>[]) {
    homePageEntries
      .filter((entry) => ModelValidator.isPage(entry) || ModelValidator.isNdcProductUpdateNotification(entry))
      .forEach((entry) => {
        this.mapEntryToRevisionNotice(entry);
      });
    this.ProductUpdateNotificationTitleFormat();
  }

  private ProductUpdateNotificationTitleFormat() {
    let maxEntries: number = this.homePage.revisionNotices[0].liveDate.length < this.MAX_ENTRIES_TABLE ? this.homePage.revisionNotices.length : this.MAX_ENTRIES_TABLE;
    let firstDate = new Date(this.homePage.revisionNotices[0].liveDate);
    let secondDate = new Date(this.homePage.revisionNotices[maxEntries - 1].liveDate);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long'
    };
    if (typeof firstDate !== 'undefined' && typeof secondDate !== 'undefined') {
      let formattedFirstDate = firstDate.toLocaleDateString('en-US', options);
      let formattedSecondDate = secondDate.toLocaleDateString('en-US', options);
      if (firstDate.getMonth() + firstDate.getFullYear() === secondDate.getMonth() + secondDate.getFullYear()) {
        this.revisionNoticeTitle = `${formattedSecondDate}`;
      } else if (firstDate.getFullYear() === secondDate.getFullYear() && firstDate.getMonth() !== secondDate.getMonth()) {
        this.revisionNoticeTitle = `${formattedFirstDate.split(' ')[0]} - ${formattedSecondDate}`;
      } else {
        this.revisionNoticeTitle = `${formattedFirstDate} - ${formattedSecondDate}`;
      }
    }
  }

  private mapEntryToRevisionNotice(entry: Entry<any>) {
    if (
      typeof entry.fields['liveDate'] !== 'undefined' &&
      typeof entry.fields['status'] !== 'undefined' &&
      typeof entry.fields['url'] !== 'undefined' &&
      (typeof entry.fields['title'] !== 'undefined' || typeof entry.fields['htmlTitle'] !== 'undefined')
    ) {
      let liveDate = entry.fields['liveDate'] as string;
      if (Date.parse(liveDate)) {
        this.homePage.revisionNotices.push({
          liveDate: liveDate,
          status: entry.fields['status'] as string,
          title: (entry.fields['title'] || entry.fields['htmlTitle']) as string,
          url: entry.fields['url'] as string
        });
      }
      this.homePage.revisionNotices?.sort((a, b) => b.liveDate.localeCompare(a.liveDate));
      this.homePage.revisionNotices.splice(5);
    }
  }

  private mapCards(homePageEntries: Entry<any>[]) {
    homePageEntries
      .filter((entry) => ModelValidator.isCard(entry))
      .forEach((entry) => {
        switch (entry.fields['category']) {
          case 'user':
            this.mapEntryToUserCard(entry);
            break;
          case 'voucher':
            this.mapEntryToCreditVoucherCard(entry);
            break;
          case 'useful':
            this.mapEntryToUsefulLinkCard(entry);
            break;
          case 'updates':
            this.mapEntryToNewsSectionCard(entry);
            break;
          default:
          // console.log('new unmapped card category : ' + entry.fields.category);
        }
      });

    this.homePage.userCards.sort((a, b) => a.order - b.order);
    this.homePage.creditVoucherCards.sort((a, b) => a.order - b.order);
    this.homePage.usefulLinkCards.sort((a, b) => a.order - b.order);
    this.homePage.newsSectionCards.sort((a, b) => a.additionalOrder - b.additionalOrder);
  }

  private mapEntryToUserCard(entry: Entry<any>) {
    let body: any = entry.fields['body'] as string;
    let headIcon: any = entry.fields?.['headIcon'] as string;
    this.homePage.userCards.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      headIcon: headIcon.fields?.file?.url,
      headIconClass: entry.fields?.['headIconClass'] as string,
      body: body.content[0].content[0].value,
      buttonText: entry.fields['buttonText'] as string,
      url: entry.fields['url'] as string
    });
  }

  private mapEntryToCreditVoucherCard(entry: Entry<any>) {
    let headImage: any = entry.fields['headImage'] as string;
    let body: any = entry.fields['body'] as string;
    this.homePage.creditVoucherCards.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      headImage: headImage.fields?.file?.url,
      body: body.content[0].content[0].value || body.content[0].content[1].data.target.fields.body,
      buttonText: entry.fields['buttonText'] as string,
      url: entry.fields['url'] as string
    });
  }

  private mapEntryToUsefulLinkCard(entry: Entry<any>) {
    let headImage: any = entry.fields['headImage'] as string;
    let body: any = entry.fields['body'] as string;
    this.homePage.usefulLinkCards.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      headImage: headImage.fields?.file?.url,
      headIconClass: entry.fields['headIconClass'] as string,
      body: body.content[0].content[0].value,
      url: entry.fields['url'] as string,
      buttonText: entry.fields['buttonText'] as string
    });
  }

  private mapEntryToNewsSectionCard(entry: Entry<any>) {
    this.homePage.newsSectionCards.push({
      title: entry.fields['title'] as string,
      order: entry.fields['order'] as number,
      shortBody: entry.fields['shortBody'] as string,
      additionalOrder: entry.fields['additionalOrder'] as number,
      footText: entry.fields['footText'] as string,
      url: entry.fields['url'] as string,
      buttonText: entry.fields['buttonText'] as string
    });
  }

  private mapNewsCardLabels() {
    const query = { 'fields.title': 'HOME' };
    this.contentfulService.getPageContentWithItems(query).then((labelItems) => {
      this.NewsCardTitle = labelItems[0]?.fields.constants[0].newsTitle;
      this.NewsUrl = labelItems[0]?.fields.constants[0].newsUrl;
      this.SeeAllNews = labelItems[0]?.fields.constants[0].seeAllNews;
      this.NewsCardImage = labelItems[0]?.fields.constants[0].newsHeadImage;
      this.NDCCreditVouchers = labelItems[0]?.fields.constants[1].ndcCreditVouchers;
      this.LatestAPIProductUpdates = labelItems[0]?.fields.constants[2].latestAPIProductUpdates;
      this.UsefulLinks = labelItems[0]?.fields.constants[3].usefulLinks;
      this.RevisionNoticeButton = labelItems[0]?.fields.constants[4].revisionNoticeButton;
      this.RevisionNoticeUrl = labelItems[0]?.fields.constants[4].revisionNoticeUrl;
    });
  }
}
